import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { formatINR, formatCompactINR } from '../data';

const Slider = ({ label, value, min, max, step, onChange, display, testId }) => (
    <div>
        <div className="flex items-center justify-between gap-3 mb-3">
            <label className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-400 shrink-0">
                {label}
            </label>
            <span className="font-display font-bold text-emerald-400 text-sm sm:text-base text-right tabular-nums whitespace-nowrap">
                {display}
            </span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            data-testid={testId}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ '--fill': `${((value - min) / (max - min)) * 100}%` }}
            className="w-full"
        />
    </div>
);

const ResultCell = ({ label, value, testId }) => (
    <div className="min-w-0">
        <div className="font-mono-x text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-slate-500">
            {label}
        </div>
        <div
            data-testid={testId}
            title={formatINR(value)}
            className="font-display text-sm sm:text-xl lg:text-2xl font-bold mt-1 break-words leading-snug tabular-nums"
        >
            {formatCompactINR(value)}
        </div>
    </div>
);

export const Calculator = () => {
    const [amount, setAmount] = useState(1000000);
    const [rate, setRate] = useState(10.5);
    const [months, setMonths] = useState(36);

    const { emi, totalInterest, totalPayment } = useMemo(() => {
        const r = rate / 1200;
        const factor = Math.pow(1 + r, months);
        const emiValue = (amount * r * factor) / (factor - 1);
        const total = emiValue * months;
        return { emi: emiValue, totalInterest: total - amount, totalPayment: total };
    }, [amount, rate, months]);

    return (
        <section
            id="calculator"
            data-testid="calculator-section"
            className="py-24 sm:py-32 bg-[#0D111A] overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="min-w-0"
                >
                    <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
                        04 — EMI Studio
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                        Know your EMI{' '}
                        <span className="text-slate-500">before you sign anything.</span>
                    </h2>
                    <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400 max-w-md">
                        Drag the sliders. Watch the maths update in real time. Indicative only —
                        your final offer depends on the bank, but we negotiate it down for you.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 44 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl border border-white/10 bg-[#131924] p-5 sm:p-9 min-w-0 overflow-hidden"
                >
                    <div className="space-y-8">
                        <Slider
                            label="Loan amount"
                            value={amount}
                            min={50000}
                            max={10000000}
                            step={50000}
                            onChange={setAmount}
                            display={formatCompactINR(amount)}
                            testId="calc-amount-slider"
                        />
                        <Slider
                            label="Interest rate (p.a.)"
                            value={rate}
                            min={8}
                            max={20}
                            step={0.25}
                            onChange={setRate}
                            display={`${rate.toFixed(2)}%`}
                            testId="calc-rate-slider"
                        />
                        <Slider
                            label="Tenure"
                            value={months}
                            min={6}
                            max={360}
                            step={6}
                            onChange={setMonths}
                            display={`${months} mo`}
                            testId="calc-tenure-slider"
                        />
                    </div>

                    <div className="mt-10 grid grid-cols-3 gap-2 sm:gap-4 border-t border-white/10 pt-7">
                        <div className="min-w-0">
                            <div className="font-mono-x text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-slate-500">
                                Monthly EMI
                            </div>
                            <div
                                data-testid="calc-emi-result"
                                title={formatINR(emi)}
                                className="font-display text-sm sm:text-xl lg:text-2xl font-bold text-emerald-400 mt-1 break-words leading-snug tabular-nums"
                            >
                                {formatCompactINR(emi)}
                            </div>
                        </div>
                        <ResultCell label="Total interest" value={totalInterest} testId="calc-interest-result" />
                        <ResultCell label="Total payable" value={totalPayment} testId="calc-total-result" />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
